import { call, delay, getContext, put, select, setContext, takeEvery } from 'redux-saga/effects';

import dynaForms, {
    getActions as getDynaActions,
    getSelectors as getDynaSelectors,
} from '@elab/core/utils/dynaForms';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import { selectors as globalSelectors } from '@elab/core/store/global';
import { actions as uiActions } from '@elab/core/store/ui';
import { selectors as configSelectors } from '@elab/core/store/config';
import { selectors as userSelectors } from '@elab/core/store/user';
import { selectors as updatePhoneNumberSelector } from '@elab/components/connected/forms/UpdatePhoneNumberForm/redux';
import {
    actions as registrationActions,
    selectors as registrationSelectors,
} from '@elab/components/connected/forms/RegistrationForm/redux';
import {
    createActions as checkoutActions,
    getSelectors as checkoutSelectors,
} from '@elab/components/connected/forms/CheckoutForms/redux';

import actions from './actions';
import selectors from './selectors';
import types from './types';
import api from './api';

const CODE_FIELD_NAME = 'code';
const DEFAULT_VALUE = '(5';
const ERROR_CODES = {
    INVALID_CODE: '403014',
};

export function* startVerification({ payload = '' }) {
    yield put(uiActions.openDialog({ name: `verify-phone-form-dialog${payload}` }));
    yield put(actions.setDataFieldValue('code', null, true));
    yield call(sendSms);
}

export function* editPhone() {
    const state = yield select();
    const parentFormActions = yield getContext('parentActions');
    yield put(uiActions.closeDialogByName('verify-phone-form-dialog'));
    const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
    if (!isNewLoyaltyFlow) {
        if (state['checkout-registration']) {
            yield put({
                type: 'checkout-registration.SET_STEP_BACK',
            });
        } else if (state.registrationForm) {
            yield put({
                type: 'registrationForm.SET_STEP_BACK',
            });
        } else {
            yield put(parentFormActions.setStepBack());
        }
    }
}

export function* successVerified() {
    const parentFormActions = yield getContext('parentActions');
    const sessionId = yield select(selectors.getSessionId);
    yield put(parentFormActions.setDataFieldValue('phoneVerificationSessionId', sessionId));
}

export function* completeVerification() {
    yield put(uiActions.closeDialogByName('verify-phone-form-dialog'));
    yield put(uiActions.closeDialogByName('verify-phone-form-dialog__extension'));
}

export function* sendSms() {
    const parentFormSelectors = yield getContext('parentSelectors');
    const isMobileNumber = yield select(configSelectors.setMobileNumber);
    let mobileNumberField = yield select(parentFormSelectors.getDataFieldValue, 'mobileNumber'); // This would be reverted back once the BE MR merged;
    let regMobileNumberField = yield select(registrationSelectors.getUserMobile);
    let chkMobileNumberField = yield select(selectors.getCheckoutRegistrationMobile);
    mobileNumberField = regMobileNumberField || chkMobileNumberField || mobileNumberField;

    const isGuest = yield select(userSelectors.isAnonymous);
    const fieldValue = isMobileNumber && mobileNumberField ? 'mobileNumber' : 'phone';
    let phoneNumber = yield select(parentFormSelectors.getDataFieldValue, fieldValue);
    phoneNumber = phoneNumber && phoneNumber !== DEFAULT_VALUE ? phoneNumber : mobileNumberField;
    const isCheckoutFlow = yield select(updatePhoneNumberSelector.isCheckoutFlow);
    if (!isGuest || isCheckoutFlow) {
        mobileNumberField = yield select(updatePhoneNumberSelector.getPhoneNumber);
        phoneNumber = mobileNumberField;
    }
    const sendNewSmsDelay = 30000;
    const countryCode = yield select(globalSelectors.getCountry);
    try {
        const response = yield call(api.getVerificationCode, { phoneNumber, countryCode });

        if (response.meta.errorCode) {
            yield put(
                actions.errorVerification({
                    errorCode: response.meta.errorCode,
                    errorMessage: response.meta.errorMessage,
                }),
            );
        } else {
            const reference = response.result.reference;
            yield put(actions.setDataFieldValue('code', null, true));

            if (reference) {
                yield put(actions.setDataFieldValue('reference', reference));
            }

            const timeout = response.result.timeout;
            if (timeout) {
                yield put(actions.setTimeout(timeout));
            }

            if (phoneNumber) {
                phoneNumber = phoneNumber.replace(/\D/g, '');
                yield put(actions.setDataFieldValue('phoneNumber', phoneNumber));
            }

            if (countryCode) {
                yield put(actions.setDataFieldValue('countryCode', countryCode));
            }
            yield delay(sendNewSmsDelay);
            yield put(actions.changeIsRetryDisabled());
        }
    } catch (error) {
        yield put(
            actions.errorVerification({
                errorCode: 400000, // random error,
                errorMessage: 'Geçersiz telefon numarası, lütfen telefon numaranızı kontrol edin.',
            }),
        );
    }
}

export function* doSubmit(dto) {
    const response = yield call(api.validateVerificationCode, dto);

    if (response.meta.errorCode) {
        switch (response.meta.errorCode) {
            case ERROR_CODES.INVALID_CODE: {
                yield put(actions.setDataFieldError(CODE_FIELD_NAME, response.meta.errorMessage));
                break;
            }
            default:
                yield put(
                    actions.errorVerification({
                        errorCode: response.meta.errorCode,
                        errorMessage: response.meta.errorMessage,
                    }),
                );
        }
    } else if (response.result && response.result.sessionId) {
        const phoneVerificationFieldFlag = yield select(selectors.getShowVerificationFieldFlag);
        yield put(
            actions.successVerified({
                sessionId: response.result.sessionId,
            }),
        );
        if (phoneVerificationFieldFlag) {
            // yield put(actions.setDataFieldValue('code', null, true));
            yield put(
                actions.setDataFieldReadonlyStatus({
                    name: 'code',
                    status: true,
                }),
            );
            yield delay(500);
            yield put(actions.changeIsRetryDisabled());
        }
    }
}

export function* showPhoneVerificationField() {
    yield put(actions.setDataFieldValue('code', null, true));
    yield call(sendSms);
}

export function* resetForm({ payload }) {
    if (payload.parentFormId === 'registrationForm') {
        const metadata = yield select(registrationSelectors.getMetadata);
        const stepMetaDataFields = metadata[0].fields;
        for (const field in stepMetaDataFields) {
            if (stepMetaDataFields[field].type !== 'HIDDEN') {
                if (stepMetaDataFields[field].name === 'mobileNumber') {
                    yield put(registrationActions.setDataFieldValue(field, '(5', true));
                } else {
                    yield put(registrationActions.setDataFieldValue(field, null, true));
                }
            }
        }
        yield put(actions.setDataFieldValue('code', null, true));
        yield put(registrationActions.phoneDobReceived({ isLoyalty: false, forceReadOnly: false }));
        yield put(registrationActions.disableStep1Button({ disableStep1Button: false }));
    } else if (payload.parentFormId === 'checkout-registration') {
        const metadata = yield select(checkoutSelectors('checkout-registration').getMetadata);
        const stepMetaDataFields = metadata[0].fields;
        for (const field in stepMetaDataFields) {
            if (stepMetaDataFields[field].type !== 'HIDDEN') {
                if (stepMetaDataFields[field].name === 'mobileNumber') {
                    yield put(
                        checkoutActions('checkout-registration').setDataFieldValue(
                            field,
                            '(5',
                            true,
                        ),
                    );
                } else {
                    yield put(
                        checkoutActions('checkout-registration').setDataFieldValue(
                            field,
                            null,
                            true,
                        ),
                    );
                }
            }
        }
        yield put(actions.setDataFieldValue('code', null, true));
        yield put(
            checkoutActions('checkout-registration').disableStep1Button({
                disableStep1Button: false,
            }),
        );

        yield put(
            checkoutActions('checkout-registration').phoneDobReceived({
                isLoyalty: false,
                forceReadOnly: false,
            }),
        );
    }
}

export default (storeName, parentFormId) => formId => {
    const parentActions = getDynaActions(parentFormId);
    const parentSelectors = getDynaSelectors(parentFormId);
    let context = setContext({ parentActions, parentSelectors, parentFormId });
    if (!parentFormId) {
        context = {};
    }
    return [
        context,
        ...dynaForms.createSagaFor(
            storeName,
            dynaFormsApi.getFormMetadata(formId || storeName),
            doSubmit,
            null,
            true,
        ),
        takeEvery(types.START_VERIFICATION, startVerification),
        takeEvery(types.EDIT_PHONE, editPhone),
        takeEvery(types.RESTART_VERIFICATION, sendSms),
        takeEvery(types.SUCCESS_VERIFIED, successVerified),
        takeEvery(types.COMPLETE_VERIFICATION, completeVerification),
        takeEvery(types.SHOW_PHONE_VERIFICATION_FIELD, showPhoneVerificationField),
        takeEvery(types.RESET_FORM, resetForm),
    ];
};
